'use strict';

/**
 * WCAG-compliant form validation script
 * Use data-message attribute for custom validation messages
 */

const FORM_SELECTOR = 'form[data-validate]';
let liveRegion = null;
let isInitialized = false;

function createLiveRegion() {
  let region = document.getElementById('form-live-region');
  if (region) {
    return region;
  }

  region = document.createElement('div');
  region.id = 'form-live-region';
  region.setAttribute('aria-live', 'polite');
  region.setAttribute('aria-atomic', 'true');
  region.className = 'visually-hidden';
  region.style.cssText = 'position:absolute;left:-10000px;width:1px;height:1px;overflow:hidden;';
  document.body.appendChild(region);

  return region;
}

function announce(message) {
  if (!liveRegion) {
    liveRegion = createLiveRegion();
  }

  liveRegion.textContent = message;

  setTimeout(() => {
    liveRegion.textContent = '';
  }, 1000);
}

function getLabel(input) {
  let label = document.querySelector(`label[for="${input.id}"]`);

  if (!label) {
    label = input.closest('label');
  }

  return label;
}

function isRequired(input, label) {
  return input.hasAttribute('required') || label.hasAttribute('required');
}

function getErrorMessage(label, type) {
  const specificMessage = label.getAttribute(`data-message-${type}`);
  if (specificMessage) return specificMessage;

  const dataMessage = label.getAttribute('data-message');
  if (dataMessage) return dataMessage;

  return null;
}

function validateByType(input, label) {
  const value = input.value.trim();
  const type = input.getAttribute('type');
  const customMessage = getErrorMessage(label, 'invalid');

  if (type === 'email') {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      return { isValid: false, message: customMessage || 'Please enter a valid email address' };
    }
  }

  if (type === 'url') {
    try {
      new URL(value);
    } catch {
      return { isValid: false, message: customMessage || 'Please enter a valid URL' };
    }
  }

  if (type === 'number') {
    const num = parseFloat(value);
    if (isNaN(num)) {
      return { isValid: false, message: customMessage || 'Please enter a valid number' };
    }

    const min = input.getAttribute('min');
    const max = input.getAttribute('max');

    if (min !== null && num < parseFloat(min)) {
      return { isValid: false, message: customMessage || `Value must be at least ${min}` };
    }

    if (max !== null && num > parseFloat(max)) {
      return { isValid: false, message: customMessage || `Value must be no more than ${max}` };
    }
  }

  const pattern = input.getAttribute('pattern');
  if (pattern) {
    const regex = new RegExp(pattern);
    if (!regex.test(value)) {
      return {
        isValid: false,
        message: customMessage || input.getAttribute('title') || 'Please match the requested format'
      };
    }
  }

  const minlength = input.getAttribute('minlength');
  if (minlength && value.length < parseInt(minlength)) {
    return { isValid: false, message: customMessage || `Please enter at least ${minlength} characters` };
  }

  const maxlength = input.getAttribute('maxlength');
  if (maxlength && value.length > parseInt(maxlength)) {
    return { isValid: false, message: customMessage || `Please enter no more than ${maxlength} characters` };
  }

  return { isValid: true };
}

function showError(input, label, message, errorType) {
  input.setAttribute('aria-invalid', 'true');

  label.querySelectorAll('.form-error').forEach(span => span.remove());

  const errorId = `${input.id}-error`;
  const errorClass = errorType === 'required' ? 'form-error form-error--required' : 'form-error form-error--invalid';
  const errorSpan = document.createElement('span');
  errorSpan.id = errorId;
  errorSpan.className = errorClass;
  errorSpan.textContent = message;
  label.appendChild(errorSpan);

  input.setAttribute('aria-describedby', errorId);
  input.classList.add('has-error');
}

function clearFieldError(input) {
  input.removeAttribute('aria-invalid');
  input.removeAttribute('aria-describedby');
  input.classList.remove('has-error');

  const label = getLabel(input);
  if (label) {
    const errorSpans = label.querySelectorAll('.form-error');
    errorSpans.forEach(span => span.remove());
  }
}

function validateField(input) {
  const label = getLabel(input);
  if (!label) return true;

  let errorMessage = '';

  if (isRequired(input, label)) {
    if (!input.value.trim()) {
      errorMessage = getErrorMessage(label, 'required') || 'This field is required';
      showError(input, label, errorMessage, 'required');
      return false;
    }
  }

  if (input.value.trim()) {
    const validationResult = validateByType(input, label);
    if (!validationResult.isValid) {
      showError(input, label, validationResult.message, 'invalid');
      return false;
    }
  }

  clearFieldError(input);
  return true;
}

function handleBlur(event) {
  const form = event.target.closest(FORM_SELECTOR);
  if (!form) return;
  const input = event.target;
  if (input.matches('input, textarea, select')) {
    validateField(input);
  }
}

function handleInput(event) {
  const form = event.target.closest(FORM_SELECTOR);
  if (!form) return;
  const input = event.target;
  if (input.matches('input, textarea, select') && input.hasAttribute('aria-invalid')) {
    clearFieldError(input);
  }
}

function handleSubmit(event) {
  const form = event.target.closest(FORM_SELECTOR);

  if (!form) return;

  event.preventDefault();

  const inputs = form.querySelectorAll('input, textarea, select');
  let isValid = true;
  const invalidFields = [];

  inputs.forEach(input => {
    if (!validateField(input)) {
      isValid = false;
      invalidFields.push(input);
    }
  });

  if (!isValid) {
    invalidFields[0].focus();
    const errorCount = invalidFields.length;
    announce(`Form validation failed. ${errorCount} ${errorCount === 1 ? 'field has an error' : 'fields have errors'}.`);
  } else {
    announce('Form validated successfully.');
    form.submit();
  }

  return isValid;
}

export function init() {
  const forms = document.querySelectorAll(FORM_SELECTOR);

  if (forms.length === 0) return;
  if (isInitialized) return;

  liveRegion = createLiveRegion();

  forms.forEach(form => {
    form.setAttribute('novalidate', '');
  });

  document.addEventListener('submit', handleSubmit, false);
  document.addEventListener('blur', handleBlur, true);
  document.addEventListener('input', handleInput, false);

  isInitialized = true;
}

export default init;
